import React, {
  FC,
  RefObject,
  createContext,
  useContext,
  useState,
} from 'react'

import { isNonProductionBuild } from 'utils/environment'

interface ActiveComment {
  inputRef?: RefObject<HTMLInputElement>
  cancel?: () => void
  initialValue: string
  currentValue?: string
  isDirty?: boolean
}

export interface ActivityCommentsContextValues {
  activeComment: ActiveComment
  setActiveComment: (comment: ActiveComment) => void
  updateValue: (value: string) => void
  resetActiveComment: () => void
}

const ActivityCommentsContext = createContext<
  ActivityCommentsContextValues | undefined
>(undefined)

export const ActivityCommentsProvider: FC = ({ children }) => {
  const [activeComment, setActiveCommentState] = useState<ActiveComment>({
    initialValue: '',
    currentValue: '',
    isDirty: false,
  })

  const setActiveComment = ({
    inputRef,
    cancel,
    initialValue,
  }: ActiveComment) => {
    setActiveCommentState({ inputRef, cancel, initialValue })
  }

  const updateValue = (value: string) => {
    const isDirty = activeComment.initialValue !== value

    setActiveCommentState(currentActiveComment => ({
      ...currentActiveComment,
      initialValue: isDirty ? currentActiveComment.initialValue : value,
      currentValue: value,
      isDirty,
    }))
  }

  const resetActiveComment = () => {
    setActiveCommentState({
      initialValue: '',
      currentValue: '',
      isDirty: false,
    })
  }

  return (
    <ActivityCommentsContext.Provider
      value={{
        activeComment,
        updateValue,
        resetActiveComment,
        setActiveComment,
      }}>
      {children}
    </ActivityCommentsContext.Provider>
  )
}

export function useActivityComments() {
  const context = useContext(ActivityCommentsContext)!

  if (isNonProductionBuild() && context === undefined) {
    throw new Error(
      'activityCommentContext must be used within a ActivityCommentProvider'
    )
  }

  return context
}
